// React
import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import moment from "moment";

// MUI
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

const PREFIX = "Additionals";

const classes = {
    btnRoot: `${PREFIX}-btnRoot`,
    buttonContainer: `${PREFIX}-buttonContainer`,
    switch: `${PREFIX}-switch`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.btnRoot}`]: {
        border: "1px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "#2f3190",
            border: "1px solid #2f3190",
        },
    },
    [`& .${classes.buttonContainer}`]: {
        display: "flex",
        justifyContent: "flex-end",
        padding: 15,
    },
    [`& .${classes.switch}`]: {
        "& .MuiSwitch-switchBase .MuiSwitch-thumb": {
            color: "#FFF",
        },
        "& .MuiSwitch-switchBase + .MuiSwitch-track": {
            backgroundColor: "#031851",
        },
        "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
            color: "#FF5968",
        },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#FFA8B1",
        },
    },
}));

const LightTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: "rgba(19, 31, 62, 0.80)",
        fontSize: 12,
        maxWidth: 320,
        borderRadius: 6,
        fontWeight: "normal",
    },
});

const NumberFormatCustom = (props) => {
    const todos = useSelector((state) => state.todos);

    const { inputRef, value, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            value={value}
            getInputRef={inputRef}
            onValueChange={({ value: v }) => onChange({ target: { value: v } })}
            thousandSeparator={todos.amountFormat === "de-DE" ? "." : ","}
            decimalSeparator={todos.amountFormat === "de-DE" ? "," : "."}
            fixedDecimalScale={true}
            decimalScale={2}
            valueIsNumericString
        />
    );
};

export const Additionals = (props) => {
    // Properties
    const { addHourFee, setAutoAlertType, setShow, setTexto, set, sumTotal, values, isError, isErrorFocus,setIsErrorFocus } = props;
    
    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component functions */
    const handleChange = (event) => {
        set({
            ...values,
            [event.target.name]: event.target.value,
        });
        setIsErrorFocus(false)
    };

    const handleChangeHoursAcc = (event) => {
        set({
            ...values,
            [event.target.name]: event.target.value,
            recuBaseRate: event.target.value * addHourFee,
            recuProjectTotal: event.target.value * addHourFee * (1 - values.recuDiscount / 100),
        });
        setIsErrorFocus(false)
    };

    const handleChangeCheck = (event, value) => {
        switch (value) {
            case 0:
                set({
                    ...values,
                    checkedExe: event.target.checked,
                    tothExe: 0,
                    startDateExe: event.target.checked ? values.initDate : null,
                    finishDateExe: null,
                });
                break;
            case 1:
                set({
                    ...values,
                    checkedCons: event.target.checked,
                    tothCons: 0,
                    startDateCons: event.target.checked ? values.initDate : null,
                    finishDateCons: null,
                });
                break;
            case 2:
                set({
                    ...values,
                    checkedRep: event.target.checked,
                    tothRep: 0,
                    startDateRep: event.target.checked ? values.initDate : null,
                    finishDateRep: null,
                });
                break;
            case 10:
                set({
                    ...values,
                    checkedOthers: event.target.checked,
                    nameOthers: "",
                    tothOthers: 0,
                    startDateOthers: event.target.checked ? values.initDate : null,
                    finishDateOthers: null,
                });
                break;
            case 11:
                set({
                    ...values,
                    checkedCont: event.target.checked,
                    tothCont: 0,
                    startDateCont: event.target.checked ? values.initDate : null,
                    finishDateCont: null,
                    projectTotal: 0,
                });
                break;
            default:
                break;
        }
        setIsErrorFocus(false)
    };

    const handleChangeStartDateAdd = (date, val) => {
        let startDate = moment(date).startOf("month");
        switch (val) {
            case 0:
                if (!date || !values.finishDateExe) {
                    set({
                        ...values,
                        startDateExe: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateExe))) {
                        setTexto(t("reports.initDateMess"));
                        setAutoAlertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateExe: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 1:
                if (!date || !values.finishDateCons) {
                    set({
                        ...values,
                        startDateCons: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateCons))) {
                        setTexto(t("reports.initDateMess"));
                        setAutoAlertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateCons: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 2:
                if (!date || !values.finishDateRep) {
                    set({
                        ...values,
                        startDateRep: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateRep))) {
                        setTexto(t("reports.initDateMess"));
                        setAutoAlertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateRep: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 10:
                if (!date || !values.finishDateOthers) {
                    set({
                        ...values,
                        startDateOthers: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateOthers))) {
                        setTexto(t("reports.initDateMess"));
                        setAutoAlertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateOthers: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            default:
                if (!date || !values.finishDateCont) {
                    set({
                        ...values,
                        startDateCont: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateCont))) {
                        setTexto(t("reports.initDateMess"));
                        setAutoAlertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateCont: moment(date).startOf("month"),
                        });
                    }
                }
                break;
        }
        setIsErrorFocus(false)
    };

    const handleChangeEndDateAdd = (date, val) => {
        let endDate = moment(date).endOf("month").startOf("day");
        switch (val) {
            case 0:
                if (!date || !values.startDateExe) {
                    set({
                        ...values,
                        finishDateExe: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateExe))) {
                        setTexto(t("reports.endDateMess"));
                        setAutoAlertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateExe: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 1:
                if (!date || !values.startDateCons) {
                    set({
                        ...values,
                        finishDateCons: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateCons))) {
                        setTexto(t("reports.endDateMess"));
                        setAutoAlertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateCons: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 2:
                if (!date || !values.startDateRep) {
                    set({
                        ...values,
                        finishDateRep: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateRep))) {
                        setTexto(t("reports.endDateMess"));
                        setAutoAlertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateRep: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 10:
                if (!date || !values.startDateOthers) {
                    set({
                        ...values,
                        finishDateOthers: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateOthers))) {
                        setTexto(t("reports.endDateMess"));
                        setAutoAlertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateOthers: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            default:
                if (!date || !values.startDateCont) {
                    set({
                        ...values,
                        finishDateCont: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateCont))) {
                        setTexto(t("reports.endDateMess"));
                        setAutoAlertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateCont: moment(date).endOf("month"),
                        });
                    }
                }
                break;
        }
        setIsErrorFocus(false)
    };

    return (
        <StyledContainer>
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={t("language.locale")}
                localeText={{ clearButtonLabel: t("miscellaneous.clear") }}
            >
                <Card elevation={0} sx={{ backgroundColor: "inherit" }}>
                    <CardHeader title={t("proposals.additionals")} />
                    <Divider />
                    <CardContent sx={{ pb: 5 }}>
                        <Grid container spacing={2} alignItems="center">
                            {values.recuAdditionalsOnly && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedCont"
                                                    checked={values.checkedCont}
                                                    onChange={() => {
                                                        return false;
                                                    }}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                />
                                            }
                                            label="Controller"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={!values.checkedCont}
                                            label={t("miscellaneous.hoursMonth")}
                                            name="tothCont"
                                            onChange={handleChangeHoursAcc}
                                            value={values.tothCont}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 0,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedCont && isError && values.tothCont === 0}                          
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                            inputRef={(input) => { (input &&isErrorFocus &&values.checkedCont && isError && values.tothCont === 0) && input.focus()}}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedCont}
                                            value={values.startDateCont}
                                            minDate={moment(values.initDate).startOf("month")}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 11)}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedCont}
                                                    {...params}
                                                    error={values.checkedCont && isError && !values.startDateCont}
                                                />
                                            )}
                                            // inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                            inputProps={{
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                            inputRef={(input) => { (input &&isErrorFocus &&values.checkedCont && isError && !values.startDateCont) && input.focus()}}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedCont}
                                            value={values.finishDateCont}
                                            minDate={moment(values.initDate).startOf("month")}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 11)}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => <TextField fullWidth {...params} />}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} md={3}>
                                <LightTooltip title={t("services.executionTaxPlanDet")}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="checkedExe"
                                                checked={values.checkedExe}
                                                onChange={(value) => handleChangeCheck(value, 0)}
                                                inputProps={{
                                                    "aria-label": "controlled",
                                                }}
                                                disabled={!values.initDate}
                                            />
                                        }
                                        label={t("services.executionTaxPlan")}
                                    />
                                </LightTooltip>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedExe}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothExe"
                                    onChange={handleChange}
                                    value={values.tothExe}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedExe && isError && values.tothExe === 0}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input &&isErrorFocus &&values.checkedExe && isError && values.tothExe === 0) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedExe}
                                    value={values.startDateExe}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 0)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedExe}
                                            {...params}
                                            error={values.checkedExe && isError && !values.startDateExe}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input &&isErrorFocus &&values.checkedExe && isError && !values.startDateExe) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedExe}
                                    value={values.finishDateExe}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 0)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => <TextField fullWidth {...params} />}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <LightTooltip title={t("services.consolidationStatementsDet")}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="checkedCons"
                                                checked={values.checkedCons}
                                                onChange={(value) => handleChangeCheck(value, 1)}
                                                inputProps={{
                                                    "aria-label": "controlled",
                                                }}
                                                disabled={!values.initDate}
                                            />
                                        }
                                        label={t("services.consolidationStatements")}
                                    />
                                </LightTooltip>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedCons}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothCons"
                                    onChange={handleChange}
                                    value={values.tothCons}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedCons && isError && values.tothCons === 0}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input &&isErrorFocus &&values.checkedCons && isError && values.tothCons === 0) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedCons}
                                    value={values.startDateCons}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 1)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedCons}
                                            {...params}
                                            error={values.checkedCons && isError && !values.startDateCons}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input &&isErrorFocus &&values.checkedCons && isError && !values.startDateCons) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedCons}
                                    value={values.finishDateCons}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 1)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => <TextField fullWidth {...params} />}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <LightTooltip title={t("services.reportsAuditorsDet")}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="checkedRep"
                                                checked={values.checkedRep}
                                                onChange={(value) => handleChangeCheck(value, 2)}
                                                inputProps={{
                                                    "aria-label": "controlled",
                                                }}
                                                disabled={!values.initDate}
                                            />
                                        }
                                        label={t("services.reportsAuditors")}
                                    />
                                </LightTooltip>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedRep}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothRep"
                                    onChange={handleChange}
                                    value={values.tothRep}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedRep && isError && values.tothRep === 0}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input &&isErrorFocus &&values.checkedRep && isError && values.tothRep === 0) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedRep}
                                    value={values.startDateRep}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 2)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedRep}
                                            {...params}
                                            error={values.checkedRep && isError && !values.startDateRep}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input &&isErrorFocus &&values.checkedRep && isError && !values.startDateRep) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedRep}
                                    value={values.finishDateRep}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 2)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => <TextField fullWidth {...params} />}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3} xl={2} sx={{ paddingLeft: "5px !important" }}>
                                        <Checkbox
                                            name="checkedOthers"
                                            checked={values.checkedOthers}
                                            onChange={(value) => handleChangeCheck(value, 10)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={!values.initDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={9} xl={10}>
                                        <TextField
                                            fullWidth
                                            disabled={!values.checkedOthers}
                                            label={t("services.others")}
                                            name="nameOthers"
                                            onChange={handleChange}
                                            value={values.nameOthers}
                                            variant="outlined"
                                            required={values.checkedOthers}
                                            error={values.checkedOthers && isError && values.nameOthers === ""}
                                            // inputProps={{
                                            //     onKeyDown: (event) => {
                                            //         event.preventDefault();
                                            //     },
                                            // }}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                            inputRef={(input) => { (input &&isErrorFocus &&values.checkedOthers && isError && values.nameOthers === "") && input.focus()}}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedOthers}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothOthers"
                                    onChange={handleChange}
                                    value={values.tothOthers}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedOthers && isError && values.tothOthers === 0}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input &&isErrorFocus &&values.checkedOthers && isError && values.tothOthers === 0) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedOthers}
                                    value={values.startDateOthers}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 10)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedOthers}
                                            {...params}
                                            error={values.checkedOthers && isError && !values.startDateOthers}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input &&isErrorFocus &&values.checkedOthers && isError && !values.startDateOthers) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedOthers}
                                    value={values.finishDateOthers}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 10)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => <TextField fullWidth {...params} />}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}></Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled
                                    label={t("services.hoursTotal")}
                                    value={sumTotal}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-hourlyRate">
                                        {t("services.hourlyRate")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-hourlyRate"
                                        value={addHourFee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.hourlyRate")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-hourlyRateAdd">
                                        {t("services.hourlyRateAdd")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-hourlyRateAdd"
                                        value={addHourFee * sumTotal}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.hourlyRateAdd")}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </LocalizationProvider>
        </StyledContainer>
    );
};
